import { FC, HTMLAttributes } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { TooltipProvider } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { TextWithTooltips } from './text-with-tooltips/text-with-tooltips'

type MarkdownRendererProps = {
  children: string
} & HTMLAttributes<HTMLDivElement>

const MarkdownRenderer: FC<MarkdownRendererProps> = ({ children: chi, ...props }) => {
  return (
    <TooltipProvider>
      <div {...props} className={cn('text-text-primary', props.className)}>
        <Markdown
          components={{
            a: ({ children, href }) => {
              return (
                <a
                  // Underline first: colour alone must not be the only cue that
                  // something is a link (WCAG 1.4.1), and it survives both themes.
                  // break-words keeps long URLs inside the narrow chat sidebar.
                  className="text-brand-funkia dark:text-brand-funkia-dark underline underline-offset-4 break-words hover:no-underline"
                  href={href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <TextWithTooltips>{children}</TextWithTooltips>
                </a>
              )
            },
            blockquote: ({ children }) => {
              return (
                <blockquote className={`my-4 border-l-4 border-gray-400 pl-4 italic `}>
                  {children}
                </blockquote>
              )
            },
            code: ({ children }) => {
              return (
                <pre className={`my-2 overflow-x-auto rounded bg-layer-2 p-3 text-sm `}>
                  <code>{children}</code>
                </pre>
              )
            },
            em: ({ children }) => {
              return (
                <em className="italic">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </em>
              )
            },
            h1: ({ children }) => {
              return (
                <h1 className="mt-4 first:mt-0 mb-2 text-base font-semibold">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </h1>
              )
            },
            h2: ({ children }) => {
              return (
                <h2 className="mt-4 first:mt-0 mb-2 text-base font-semibold">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </h2>
              )
            },
            h3: ({ children }) => {
              return (
                <h3 className="mt-4 first:mt-0 mb-2 text-base font-semibold">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </h3>
              )
            },
            h4: ({ children }) => {
              return (
                <h4 className="mt-4 first:mt-0 mb-2 text-base font-semibold">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </h4>
              )
            },
            h5: ({ children }) => {
              return (
                <h5 className="mt-4 first:mt-0 mb-2 text-base font-semibold">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </h5>
              )
            },
            h6: ({ children }) => {
              return (
                <h6 className="mt-4 first:mt-0 mb-2 text-base font-semibold">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </h6>
              )
            },
            li: ({ children }) => {
              return (
                <li className="my-1 text-base">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </li>
              )
            },
            ol: ({ children }) => {
              return <ol className="my-2 list-decimal pl-5 text-sm">{children}</ol>
            },
            p: ({ children }) => {
              return (
                <p className="my-5 text-base first:mt-0 last:mb-0">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </p>
              )
            },
            strong: ({ children }) => {
              return (
                <strong className="font-semibold">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </strong>
              )
            },
            table: ({ children }) => {
              return (
                <table className="my-4 w-full table-auto border-collapse text-sm">{children}</table>
              )
            },
            tbody: ({ children }) => <tbody>{children}</tbody>,
            td: ({ children }) => {
              return (
                <td className="px-4 py-2 align-top">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </td>
              )
            },
            th: ({ children }) => {
              return (
                <th className="px-4 py-2 text-left font-semibold">
                  <TextWithTooltips>{children}</TextWithTooltips>
                </th>
              )
            },
            thead: ({ children }) => {
              return <thead className="bg-gray-100">{children}</thead>
            },
            tr: ({ children }) => {
              return <tr className="border-b border-gray-200">{children}</tr>
            },
            ul: ({ children }) => {
              return <ul className="mt-2 mb-6 list-disc pl-5 text-sm">{children}</ul>
            },
          }}
          remarkPlugins={[remarkGfm]}
        >
          {chi}
        </Markdown>
      </div>
    </TooltipProvider>
  )
}

export default MarkdownRenderer
